import {CroppedImageRequestPayload, CroppedImageResponse} from '@/api/MLSChallenge/getCroppedImage';
import {Button} from '@/components/MLS/Button/Button';
import Loader from '@/components/MLS/Loader/Loader';
import React, {memo, useCallback, useMemo, useState} from 'react';
import Cropper from 'react-easy-crop';
import {Area, Point} from 'react-easy-crop/types';
import {FaInfoCircle} from 'react-icons/fa';
import {toast} from 'react-toastify';

const CROP_AREA_SIZE = 250;

// New constants for dynamic pan restriction
const TARGET_TOTAL_PAN_RANGE_RATIO = 0.8; // Target: total pannable distance is 80% of crop area dimension
const MIN_DRAG_REDUCTION_FACTOR = 0.1; // Most restrictive factor (e.g., for very large images)
const MAX_DRAG_REDUCTION_FACTOR = 0.8; // Least restrictive factor (maintains current feel for smaller images)

interface CropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedImage: string) => void;
  getCroppedImage: (payload: CroppedImageRequestPayload) => Promise<CroppedImageResponse>;
  onRevertOriginal: (() => void) | null;
}

export const CropModal: React.FC<CropModalProps> = memo(
  ({isOpen, imageSrc, onClose, onCropComplete, getCroppedImage, onRevertOriginal}) => {
    const minZoom = 0.5;
    const maxZoom = 2;
    const [crop, setCrop] = useState({x: 0, y: 0});
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const MIN_OVERFLOW_PERCENT = 0.5; // 10%
    const [naturalImageSize, setNaturalImageSize] = useState<{width: number; height: number} | null>(null);
    const revertCrop = useCallback(() => {
      setCrop({x: 0, y: 0});
      setZoom(1);
    }, []);

    const avatarChanged = useMemo(() => {
      return crop.x !== 0 || crop.y !== 0 || zoom !== 1;
    }, [crop, zoom, croppedAreaPixels]);

    const onCropCompleteCallback = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Callback for when the media (image) is loaded by Cropper
    const onMediaLoaded = useCallback(
      (mediaSize: {width: number; height: number; naturalWidth: number; naturalHeight: number}) => {
        console.log('onMediaLoaded: ', mediaSize);
        setNaturalImageSize({width: mediaSize.naturalWidth, height: mediaSize.naturalHeight});
      },
      []
    );

    // Update croppedAreaPixels when crop is complete
    const onCropChange = useCallback(
      (newCrop: Point) => {
        if (!naturalImageSize || zoom === null) {
          setCrop(newCrop);
          return;
        }

        const {width: imgNaturalW, height: imgNaturalH} = naturalImageSize;
        const cropAreaW = CROP_AREA_SIZE;
        const cropAreaH = CROP_AREA_SIZE;

        let clampedX = newCrop.x;
        let clampedY = newCrop.y;

        // Horizontal clamping
        const imgZoomedW = imgNaturalW * zoom;
        if (imgZoomedW > cropAreaW) {
          const maxDeltaX = (imgZoomedW - cropAreaW) / 2;
          const excessWidth = imgZoomedW - cropAreaW;

          let dynamicFactorX;
          if (excessWidth > 0) {
            const calculatedFactor = (TARGET_TOTAL_PAN_RANGE_RATIO * cropAreaW) / excessWidth;
            dynamicFactorX = Math.max(MIN_DRAG_REDUCTION_FACTOR, Math.min(MAX_DRAG_REDUCTION_FACTOR, calculatedFactor));
          } else {
            // Should not be reached if imgZoomedW > cropAreaW, but as a fallback
            dynamicFactorX = MAX_DRAG_REDUCTION_FACTOR;
            console.log('dynamicFactorX: ', dynamicFactorX);
          }
          const reducedMaxDeltaX = maxDeltaX * dynamicFactorX;
          clampedX = Math.max(-reducedMaxDeltaX, Math.min(reducedMaxDeltaX, newCrop.x));
        } else {
          clampedX = 0; // Center if image is narrower than crop area
        }

        // Vertical clamping
        const imgZoomedH = imgNaturalH * zoom;
        if (imgZoomedH > cropAreaH) {
          const maxDeltaY = (imgZoomedH - cropAreaH) / 2;
          const excessHeight = imgZoomedH - cropAreaH;

          let dynamicFactorY;
          if (excessHeight > 0) {
            const calculatedFactor = (TARGET_TOTAL_PAN_RANGE_RATIO * cropAreaH) / excessHeight;
            dynamicFactorY = Math.max(MIN_DRAG_REDUCTION_FACTOR, Math.min(MAX_DRAG_REDUCTION_FACTOR, calculatedFactor));
          } else {
            // Should not be reached if imgZoomedH > cropAreaH, but as a fallback
            dynamicFactorY = MAX_DRAG_REDUCTION_FACTOR;
            console.log('dynamicFactorY: ', dynamicFactorY);
          }
          const reducedMaxDeltaY = maxDeltaY * dynamicFactorY;
          clampedY = Math.max(-reducedMaxDeltaY, Math.min(reducedMaxDeltaY, newCrop.y));
        } else {
          clampedY = 0; // Center if image is shorter than crop area
        }
        setCrop({x: clampedX, y: clampedY});
      },
      [naturalImageSize?.width, naturalImageSize?.height, zoom, setCrop]
    );

    const handleApply = useCallback(async () => {
      console.log('handleApply: ', croppedAreaPixels);
      if (!croppedAreaPixels) {
        return;
      }
      try {
        setIsLoading(true);
        const croppedImage = await getCroppedImage({imageSrc, croppedArea: croppedAreaPixels});
        console.log('croppedImage: ', croppedImage);
        onCropComplete(croppedImage.croppedImageDataUrl);
      } catch (e) {
        console.error(e);
        toast.error('Failed to crop image. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }, [croppedAreaPixels, imageSrc, onCropComplete, getCroppedImage]);

    const increaseZoom = useCallback(() => {
      setZoom((prevZoom) => Math.min(prevZoom + 0.1, maxZoom));
    }, [maxZoom]);

    const decreaseZoom = useCallback(() => {
      setZoom((prevZoom) => Math.max(prevZoom - 0.1, minZoom));
    }, [minZoom]);

    if (!isOpen) {
      return null;
    }

    return (
      <div
        data-test-id="crop-modal"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
      >
        <div data-test-id="crop-modal-content" className="relative mt-4 flex flex-col gap-4 rounded-lg bg-white p-4">
          {isLoading && (
            <div
              data-test-id="crop-modal-loader"
              className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-70"
            >
              <Loader />
            </div>
          )}
          <div
            data-test-id="crop-modal-cropper"
            className="bg-gray-700 relative mx-auto flex h-[345px] w-[345px] items-center justify-center"
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              cropSize={{width: 250, height: 250}}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              aspect={1}
              restrictPosition={false}
              cropShape="rect"
              showGrid
              onCropChange={onCropChange}
              onZoomChange={setZoom}
              onCropComplete={onCropCompleteCallback}
              onMediaLoaded={onMediaLoaded}
              classes={{
                containerClassName: 'crop-container',
                cropAreaClassName: 'crop-area',
              }}
            />
          </div>
          <div data-test-id="crop-modal-info" className="text-center text-black">
            <FaInfoCircle className="mr-1 inline-block" />
            Drag the image to adjust its position. Use pinch gestures or the zoom buttons to scale for precise cropping.
          </div>
          <div data-test-id="crop-modal-zoom-buttons" className="flex justify-center gap-4">
            <Button
              data-test-id="crop-modal-zoom-button-decrease"
              variant="dark"
              size="md"
              label="-"
              onClick={decreaseZoom}
            />
            <Button
              data-test-id="crop-modal-zoom-button-increase"
              variant="dark"
              size="md"
              label="+"
              onClick={increaseZoom}
            />
          </div>
          <div data-test-id="crop-modal-buttons" className="flex justify-center gap-2 xs:gap-4">
            <Button data-test-id="crop-modal-button-cancel" variant="dark" size="md" label="Cancel" onClick={onClose} />
            {avatarChanged && (
              <Button
                data-test-id="crop-modal-button-apply"
                variant="dark"
                size="md"
                label="Apply Edits"
                onClick={handleApply}
              />
            )}
            {avatarChanged && (
              <Button
                data-test-id="crop-modal-button-revert"
                variant="dark"
                size="md"
                label="Revert Edits"
                onClick={revertCrop}
              />
            )}
          </div>
          <div className="flex justify-center gap-4">
            {onRevertOriginal && (
              <Button
                data-test-id="crop-modal-button-revert-original"
                variant="dark"
                size="md"
                label="Revert to Initial"
                onClick={onRevertOriginal}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
CropModal.displayName = 'CropModal';
